import {coordinatesToEpsg2056, toEPSG21781} from '../map/proj.ts';
import type {LAND_LAYER_ORDER, LAYER_ORDER} from '../layers/layerConstants.ts';
import {WS_ALIASES} from '../layers/layerConstants.ts';
import {capitalize, includeBbox} from '../utils.ts';
import {BASE_API_URL} from '../envs.ts';
import {msg} from '@lit/localize';
import type {LanguageCode} from '../i18n.ts';
import {getLanguageCode} from '../i18n.ts';
import {featureFromGeoJSON} from '../map/helpers.ts';
import type Feature from 'ol/Feature';
import {
  ACCOMMODATION_TRANSLATIONS,
  STATION_TRANSLATIONS,
  WS_I18N,
} from '../layers/layerTranslations.ts';
import type {
  AccommodationCode,
  GeoQueryResponse,
  LandCode,
  LayerCode,
  QueryCategory,
  QueryResponse,
  RouteCategoryCode,
} from '../layers/layerTypes.ts';
import {
  apiCodeToLandCode,
  getLayerApiCode,
  getMultiLandCategoryKVP,
  isWinterLandCode,
  isWinterLayer,
} from '../layers/layerHelpers.ts';
import type {LineString, MultiLineString, Point, Polygon} from 'ol/geom';
import type {GeoLayerCode} from './swisstopo-types.ts';
import {
  GEO_BODID_TO_CODE,
  GEO_CODE_TO_REQUEST_LAYER,
} from './swisstopo-types.ts';
import type {TourTimeType} from '../apis/api-tours.ts';

export type QueryFeatureLayer = (typeof LAYER_ORDER)[number];

export type QueryItem = {
  title: string;
  type: string;
  layer: QueryFeatureLayer;
  olFeature: Feature;
  layers: LayerCode[];
  geometryType?: string;
  subtype?: 'wildlifearea' | 'wildlifereserve';
  bbox?: number[];
  logoSrc?: string;
  routeNumber?: number;
  segmentNumber?: number;
  land?: LandCode;
  subtitle?: string;
  category?: QueryCategory;
  tourId?: number;
  poiId?: number;
  poiIndex?: number;
  colorRgb?: string;
  timeType?: TourTimeType;
};

function applyLowerTolerance(
  features: QueryItem[],
  bbox: number[],
): QueryItem[] {
  return features.filter((q) => {
    if (q.type === 'wildlife' || !q.bbox) {
      return true;
    }
    return includeBbox(bbox, q.bbox);
  });
}

async function responseToQueryItems(response: Response): Promise<QueryItem[]> {
  const json: QueryResponse = await response.json();
  const results = json.features;
  return Promise.all(
    results.map(async (i): Promise<QueryItem> => {
      let routeNumber: number | undefined;
      let segmentNumber: number | undefined;
      let logoSrc: string | undefined;
      let layer: QueryFeatureLayer;
      const layers: LayerCode[] = [];
      const props = i.properties;
      let subtitle = '';
      const landCode: LandCode | undefined = apiCodeToLandCode[props.land];
      let category: QueryCategory;

      if (props.type === 'etappen') {
        category = <RouteCategoryCode>props.category;
        // route number = decimals before comma
        // segment number = decimals after comma
        const numbers: string[] = props.e_number.split('.');
        routeNumber = parseInt(numbers[0], 10);
        segmentNumber = parseInt(numbers[1], 10);
        subtitle = `${
          segmentNumber ? `${msg('Stage')} ${segmentNumber}: ` : ''
        }${props.start}-${props.end}`;

        if (isWinterLandCode(landCode) && isWinterLayer(props.land)) {
          layer = landCode;
          layers.push(props.land);
        } else {
          layers.push(getMultiLandCategoryKVP(props.land, category));
          layer = <(typeof LAND_LAYER_ORDER)[number]>`${landCode}.${category}`;
        }
        logoSrc = props.logo_svg || props.logo;
      } else if (props.category === 'slowup') {
        // change date format from yyyy-mm-dd to dd.mm.yyyy
        subtitle = props.date;
        const match = subtitle.match(/(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
          subtitle = `${match[3]}.${match[2]}.${match[1]}`;
        }
        category = layer = props.category;
        layers.push(layer);
      } else if (props.category === 'swisspark') {
        subtitle = WS_I18N[props.category];
        category = layer = props.category;
        layers.push(layer);
      } else {
        if (props.category === 'accomodation') {
          const types: AccommodationCode[] = props.types;
          layer = types[0];
          const translatedTypes = types.map(
            (t) => ACCOMMODATION_TRANSLATIONS[t],
          );
          subtitle = translatedTypes.join(', ');
          category = 'accommodation';
          logoSrc = `/img/poi/${capitalize(layer)}_22.svg?ts11=TIMESTAMP11`;
          layers.push(...types);
        } else if (props.category === 'stations') {
          subtitle = STATION_TRANSLATIONS[props.sc_transport];
          logoSrc = `/img/poi/${capitalize(
            props.sc_transport,
          )}_22.svg?ts11=TIMESTAMP11`;
          category = layer = 'station';
          layers.push(props.sc_transport);
        } else if (
          props.category === 'poi' ||
          props.category === 'places' ||
          props.category === 'rental' ||
          props.category === 'cycleservice'
        ) {
          const name = WS_ALIASES[props.category];
          subtitle = WS_I18N[name];
          category = layer = name;
          logoSrc = `/img/poi/${capitalize(layer)}_22.svg?ts11=TIMESTAMP11`;
          layers.push(name);
        } else if (props.category === 'detour') {
          logoSrc = `/img/detour/${
            props['symbol_intermediate'] || props['symbol_start_end']
          }.svg?ts11=TIMESTAMP11`;
          category = layer = props.category;
        }
      }

      const olFeature: Feature = featureFromGeoJSON(i);
      olFeature.set('logoSrc', logoSrc);
      const geom = <LineString | MultiLineString | Point | Polygon>(
        olFeature.getGeometry()
      );
      if (geom) {
        // todo wrong type in ol?
        geom.setCoordinates(
          <Array<any>>coordinatesToEpsg2056(geom.getCoordinates()),
        );
      }

      return {
        routeNumber,
        segmentNumber,
        title: props.label || props.title || props.name,
        subtitle,
        land: landCode,
        type: props.type,
        logoSrc: logoSrc,
        category,
        layer,
        geometryType: i.geometry?.type,
        olFeature,
        layers,
      };
    }),
  );
}

async function geoResponseToQueryItemsWithFetch(
  response: Response,
  lang: LanguageCode,
): Promise<QueryItem[]> {
  const json: GeoQueryResponse = await response.json();
  const results = json.results;
  return Promise.all(
    results.map(async (i): Promise<QueryItem> => {
      const props = i.properties;
      const layers: LayerCode[] = [];
      const type = GEO_BODID_TO_CODE[i.layerBodId];
      layers.push(type);
      const subtype =
        i.layerBodId === 'ch.bafu.wrz-jagdbanngebiete_select'
          ? 'wildlifereserve'
          : 'wildlifearea';
      const layer = type;

      if (type === 'alpguarddog' || type === 'shooting') {
        const url = `https://api3.geo.admin.ch/rest/services/api/MapServer/${GEO_CODE_TO_REQUEST_LAYER[type]}/${i.featureId}/htmlPopup?lang=${lang}`;
        i.properties.htmlPopup = await fetch(url).then((r) => r.text());
      }
      if (type === 'municipalities') {
        const url = `https://api3.geo.admin.ch/rest/services/api/MapServer/ch.swisstopo.swissboundaries3d-gemeinde-flaeche.fill/${i.featureId}/htmlPopup?lang=${lang}`;
        i.properties.htmlPopup = await fetch(url).then((r) => r.text());
      }
      if (type === 'cantons') {
        const url = `https://api3.geo.admin.ch/rest/services/api/MapServer/ch.swisstopo.swissboundaries3d-kanton-flaeche.fill/${i.featureId}/htmlPopup?lang=${lang}`;
        i.properties.htmlPopup = await fetch(url).then((r) => r.text());
      }

      const title =
        props.label ||
        props.name ||
        props[`wegtyp_${lang === 'en' ? 'de' : lang}`];
      const olFeature: Feature = featureFromGeoJSON(i);

      return {
        title,
        subtitle: i.layerName,
        type,
        subtype,
        bbox: i.bbox,
        layer,
        geometryType: i.geometry.type,
        olFeature,
        layers,
      };
    }),
  );
}

export async function fetchFeatures(
  layers: LayerCode[],
  bbox: number[],
  wildlifeToleranceBbox: number[],
) {
  const layerNames: string[] = [];
  layers.forEach((l) => {
    layerNames.push(getLayerApiCode(l));
  });
  const lang = getLanguageCode();
  const bbox21781: number[] = toEPSG21781([bbox[0], bbox[1]]).concat(
    toEPSG21781([bbox[2], bbox[3]]),
  );
  const params = new URLSearchParams({
    restriction: '15',
    attributes: 'yes',
    type: 'etappen',
    land: 'all',
    category: 'all',
    simplify: '5',
    translated: 'true',
    language: lang,
    layers: layerNames.join(','),
    bbox: bbox21781.map((c) => c.toFixed()).join(','),
  });
  const queryUrl = `${BASE_API_URL}/api/4/feature/query?${params.toString()}`;
  const queryResponse: Response = await fetch(queryUrl);
  let features: QueryItem[] = await responseToQueryItems(queryResponse);

  const swisstopoLayers: string[] = layers.reduce((acc: string[], geoCode) => {
    const bodId = GEO_CODE_TO_REQUEST_LAYER[<GeoLayerCode>geoCode];
    if (bodId) acc.push(bodId);
    return acc;
  }, []);

  if (swisstopoLayers.length > 0) {
    const params = new URLSearchParams({
      // we are querying layers that are time dependent (municipalities and cantons)
      // we need to provide a year to get the current data
      timeInstant: new Date().getFullYear().toString(),
      geometryType: 'esriGeometryEnvelope',
      imageDisplay: '500,600,96',
      mapExtent: '548945,147956,549402,148103',
      tolerance: '0',
      geometryFormat: 'geojson',
      sr: '2056',
      lang: lang,
      layers: 'all:' + swisstopoLayers.join(','),
      geometry: bbox.join(','),
    });
    const swisstopoQueryUrl = `https://api3.geo.admin.ch/rest/services/api/MapServer/identify?${params.toString()}`;
    const swisstopoResponse = await fetch(swisstopoQueryUrl);
    const swisstopoQuery: QueryItem[] = await geoResponseToQueryItemsWithFetch(
      swisstopoResponse,
      lang,
    );
    features.push(...swisstopoQuery);
  }

  if (wildlifeToleranceBbox) {
    features = applyLowerTolerance(features, wildlifeToleranceBbox);
  }

  return features;
}

export async function fetchFeaturesMultilayer(
  params: URLSearchParams,
): Promise<GeoJSON.FeatureCollection> {
  const featureCollection: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: [],
  };
  if (!params || params.size <= 0) return featureCollection;
  try {
    const response = await fetch(
      `${BASE_API_URL}/api/4/query/featuresmultilayers?${params.toString()}`,
    );
    return await response.json();
  } catch (e) {
    console.error(e);
    return featureCollection;
  }
}
